import { Injectable, Signal, signal } from "@angular/core";
import { Router } from "@angular/router";
import { TimeTracksFilters } from "../remote-data-service/repositories/time-track-repository/time-track.repository";
import { ProjectsFilters } from "../remote-data-service/repositories/project-repository/project.repository";
import { TimeTrackTagsFilters } from "../remote-data-service/repositories/tag-repository/tag.repository";
import { ClientsFilters } from "../remote-data-service/repositories/person-repository/client.repository";
import { UserFilters } from "../remote-data-service/repositories/user-repository/user.repository";
import { DateService } from "../date-service/date.service";
import { DateFiltersValues } from "src/app/components/atoms/custom-date-filter/custom-date-filter.component";

export type FormValues = Record<string, unknown>;

export interface ProjectsFormValues {
    title: string | null;
    clients: string[] | null;
    members: string[] | null;
}

export interface ReportsFormValues {
    projects: string[] | null;
    clients: string[] | null;
    tags: string[] | null;
    users: string[] | null | undefined;
}

export interface ClientsFormValues {
    name: string | null;
}

export interface UsersFormValues {
    name: string | null;
}

export interface TrackerFormValues {
    title: string | null;
    members: string[] | null;
}

export type AllFormValues =
    ProjectsFormValues |
    ReportsFormValues |
    ClientsFormValues |
    TrackerFormValues |
    UsersFormValues;

type RouteFormState = Record<string, FormValues>;


/**
 * Service that manages the filters for all entities in the application.
 *
 * This service acts as a centralized store for filter configurations:
 * 
 * - Each entity type (time tracks, projects, tags, etc.) has its own signal 
 *   storing a map of filters.
 * - The map keys are URL paths, allowing different routes to maintain their own
 *   filter states.
 * - Filter updates can be 'soft' (merge with existing) or 'hard' (complete 
 *   replace).
 * - Components can set filters through setter methods, which are then accessible
 *   via readonly signals.
 * - The ActionService can retrieve current filters through getter methods when
 *   handling events.
 */
@Injectable({
    providedIn: 'root'
})
export class FiltersService {
    /** Signal storing form values for each route and form */
    protected _formValues = signal<Map<string, RouteFormState>>(new Map());


    /** Signal storing the time tracks filters map for each route */
    protected _timeTracksFilters = signal<Map<string, TimeTracksFilters>>(new Map());


    /** Signal storing the projects filters map for each route */
    protected _projectsFilters = signal<Map<string, TimeTracksFilters>>(new Map());


    /** Signal storing the tags filters map for each route */
    protected _tagsFilters = signal<Map<string, TimeTrackTagsFilters>>(new Map());


    /** Signal storing the clients filters map for each route */
    protected _clientsFilters = signal<Map<string, ClientsFilters>>(new Map());


    /** Signal storing the users filters map for each route */
    protected _userFilters = signal<Map<string, UserFilters>>(new Map());


    /** Signal storing the initial date filter */
    protected _initDateFilter = signal<Map<string, DateFiltersValues>>(new Map());


    /**
     * Constructor that runs on every load or injection.
     */
    constructor(protected router: Router, protected dateService: DateService) {
    }

    /****************/
    /* Form methods */
    /****************/

    /**
     * Saves form values for a specific form on the current route
     * @param formId - Unique identifier for the form
     * @param values - Form values to save
     */
    public saveFormValues(formId: string, values: FormValues): void {
        const currentUrl = this.router.url;
        const currentRouteState = this._formValues().get(currentUrl) || {};

        const updatedRouteState = {
            ...currentRouteState,
            [formId]: values
        };

        this._formValues.set(new Map(this._formValues().set(
            currentUrl,
            updatedRouteState
        )));
    }

    /**
     * Retrieves saved form values for a specific form on the current route
     * @param formId - Unique identifier for the form
     * @returns The saved form values or undefined if none exist
     */
    public getFormValues<T extends AllFormValues>(formId: string): T | undefined {
        const currentUrl = this.router.url;
        const routeState = this._formValues().get(currentUrl);
        return routeState?.[formId] as T | undefined;
    }

    /**
     * Checks if there are saved form values for a specific form on the current route
     * @param formId - Unique identifier for the form
     * @returns True if form values exist, false otherwise
     */
    public hasFormValues(formId: string): boolean {
        return this.getFormValues(formId) !== undefined;
    }


    /******************/
    /* Entity methods */
    /******************/

    /**
     * Setter that adds or updates time track filters for the current route.
     * 
     * @param filters - The time track filters to apply
     * @param overwritting - Whether to completely replace ('hard') or merge with 
     *   existing ('soft') filters (default: 'soft')
     */
    public setTimeTracksFilters(filters: TimeTracksFilters, overwritting: 'hard' | 'soft' = 'soft'): void {
        const currentFilters = this._timeTracksFilters().get(this.router.url);

        let newFilters: TimeTracksFilters;
        if (overwritting === 'hard' || !currentFilters) {
            newFilters = filters;
        } else {
            // Merge existing filters with new ones in 'soft' mode
            newFilters = {
                ...currentFilters,
                ...filters
            };
        }

        this._timeTracksFilters.set(new Map(this._timeTracksFilters().set(
            this.router.url,
            newFilters
        )));
    }


    /**
     * Getter that returns the time tracks signal as read only.
     * 
     * @returns A read-only signal containing the time tracks filters map
     */
    get timeTracksFilters(): Signal<Map<string, TimeTracksFilters>> {
        return this._timeTracksFilters.asReadonly();
    }


    /**
     * Setter that adds or updates project filters for the current route.
     * 
     * @param filters - The project filters to apply
     * @param overwritting - Whether to completely replace ('hard') or merge with
     *   existing ('soft') filters (default: 'soft')
     */
    public setProjectsFilters(filters: ProjectsFilters, overwritting: 'hard' | 'soft' = 'soft'): void {
        const currentFilters = this._projectsFilters().get(this.router.url);

        let newFilters: ProjectsFilters;
        if (overwritting === 'hard' || !currentFilters) {
            newFilters = filters;
        } else {
            newFilters = {
                ...currentFilters,
                ...filters
            };
        }

        this._projectsFilters.set(new Map(this._projectsFilters().set(
            this.router.url,
            newFilters
        )));
    }


    /**
     * Getter that returns the projects signal as read only.
     * 
     * @returns A read-only signal containing the projects filters map
     */
    get projectsFilters(): Signal<Map<string, ProjectsFilters>> {
        return this._projectsFilters.asReadonly();
    }


    /**
     * Setter that adds or updates tag filters for the current route.
     * 
     * @param filters - The tag filters to apply
     * @param overwritting - Whether to completely replace ('hard') or merge with
     *   existing ('soft') filters (default: 'soft')
     */
    public setTagsFilters(filters: TimeTrackTagsFilters, overwritting: 'hard' | 'soft' = 'soft'): void {
        const currentFilters = this._tagsFilters().get(this.router.url);

        let newFilters: TimeTrackTagsFilters;
        if (overwritting === 'hard' || !currentFilters) {
            newFilters = filters;
        } else {
            newFilters = {
                ...currentFilters,
                ...filters
            };
        }

        this._tagsFilters.set(new Map(this._tagsFilters().set(
            this.router.url,
            newFilters
        )));
    }


    /**
     * Getter that returns the tags signal as read only.
     * 
     * @returns A read-only signal containing the tags filters map
     */
    get tagsFilters(): Signal<Map<string, TimeTrackTagsFilters>> {
        return this._tagsFilters.asReadonly();
    }


    /**
     * Setter that adds or updates client filters for the current route.
     * 
     * @param filters - The client filters to apply
     * @param overwritting - Whether to completely replace ('hard') or merge with
     *   existing ('soft') filters (default: 'soft')
     */
    public setClientsFilters(filters: ClientsFilters, overwritting: 'hard' | 'soft' = 'soft'): void {
        const currentFilters = this._clientsFilters().get(this.router.url);

        let newFilters: ClientsFilters;
        if (overwritting === 'hard' || !currentFilters) {
            newFilters = filters;
        } else {
            newFilters = {
                ...currentFilters,
                ...filters
            };
        }

        this._clientsFilters.set(new Map(this._clientsFilters().set(
            this.router.url,
            newFilters
        )));
    }


    /**
     * Getter that returns the clients signal as read only.
     * 
     * @returns A read-only signal containing the clients filters map
     */
    get clientsFilters(): Signal<Map<string, ClientsFilters>> {
        return this._clientsFilters.asReadonly();
    }


    /**
     * Setter that adds or updates user filters for the current route.
     * 
     * @param filters - The user filters to apply
     * @param overwritting - Whether to completely replace ('hard') or merge with
     *   existing ('soft') filters (default: 'soft')
     */
    public setUserFilters(filters: UserFilters, overwritting: 'hard' | 'soft' = 'soft'): void {
        const currentFilters = this._userFilters().get(this.router.url);

        let newFilters: UserFilters;
        if (overwritting === 'hard' || !currentFilters) {
            newFilters = filters;
        } else {
            newFilters = {
                ...currentFilters,
                ...filters
            };
        }

        this._userFilters.set(new Map(this._userFilters().set(
            this.router.url,
            newFilters
        )));
    }


    /**
     * Getter that returns the users signal as read only.
     * 
     * @returns A read-only signal containing the users filters map
     */
    get userFilters(): Signal<Map<string, UserFilters>> {
        return this._userFilters.asReadonly();
    }


    /******************/
    /* Date methods */
    /******************/

    /**
     * Setter that adds or updates the initial date filter for the current route.
     *
     * @param dateRange - The date range to apply
     */
    public setInitDateFilter(dateRange: DateFiltersValues): void {
        this._initDateFilter.set(new Map(this._initDateFilter().set(
            this.router.url,
            dateRange
        )));
    }

    /**
     * Getter that returns the initial date filter as read only.
     *
     * @returns A read-only signal containing the initial date filter
     */
    get initDateFilter(): Signal<Map<string, DateFiltersValues>> {
        return this._initDateFilter.asReadonly();
    }


}
